import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ReimbursementTypes } from '@app/enums/ReimbursementTypes';
import {
    CardComponent,
    CardEditModeContentDirective,
    CardMode,
    CardViewModeContentDirective,
} from '@components/card/card.component';
import { MdsRadioButton, MdsRadioGroupComponent } from '@medpacesoftwaredevelopment/designsystem';

import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReimbursementRequestService } from '@app/v2/services/reimbursement-request/reimbursement-request.service';
import { StipendRequestService } from '@app/v2/services/stipend-request/stipend-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { Patient } from '@models/patient';
import { PaymentRequestStatus, Reimbursement, ReimbursementRequest, StipendRequest } from '@models/request';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import { PersistentFormControl, PersistentFormGroup } from '@utility/persistent-forms';
import { combineLatest, filter, map, merge, pairwise, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs';
import {
    ReimbursementRequestEditComponent,
    ReimbursementRequestFormGroup,
} from './reimbursement-request/reimbursement-request-edit/reimbursement-request-edit.component';
import { ReimbursementRequestViewComponent } from './reimbursement-request/reimbursement-request-view/reimbursement-request-view.component';
import {
    StipendRequestEditComponent,
    StipendRequestFormGroup,
} from './stipend-request/stipend-request-edit/stipend-request-edit.component';
import { StipendRequestViewComponent } from './stipend-request/stipend-request-view/stipend-request-view.component';

@Component({
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'medpace-payment-request-card',
    imports: [
        CardComponent,
        CardEditModeContentDirective,
        CardViewModeContentDirective,
        ReimbursementRequestEditComponent,
        ReimbursementRequestViewComponent,
        MdsRadioGroupComponent,
        AsyncPipe,
        StipendRequestEditComponent,
        StipendRequestViewComponent,
    ],
    templateUrl: './payment-request-card.component.html',
})
export class PaymentRequestCardComponent {
    mode = input.required<CardMode>();
    showEditSaveButton = input.required<boolean>();
    linkedMode = linkedSignal(() => this.mode());
    formGrp = input.required<PaymentRequestCardFormGroup>();
    formGrp$ = toObservable(this.formGrp);
    activatedRoute = inject(ActivatedRoute);
    router = inject(Router);
    userService = inject(UserServiceV2);
    ReimbursementTypes = ReimbursementTypes;

    reimbursementRequest = input<ReimbursementRequest>();
    reimbursementRequest$ = toObservable(this.reimbursementRequest);
    reimbursementRequestService = inject(ReimbursementRequestService);
    reimbursementRequestEditComponent = viewChild(ReimbursementRequestEditComponent);

    stipendRequest = input<StipendRequest>();
    stipendRequest$ = toObservable(this.stipendRequest);
    stipendRequestService = inject(StipendRequestService);
    stipendRequestEditComponent = viewChild(StipendRequestEditComponent);

    private readonly stipendButton: MdsRadioButton = {
        label: 'Stipend',
        value: ReimbursementTypes.Stipend,
        name: 'request-expense-card-stipend',
        id: 'request-expense-card-stipend',
    };
    private readonly reimbursementButton: MdsRadioButton = {
        label: 'Out of Pocket Reimbursement',
        value: ReimbursementTypes.OutOfPocket,
        name: 'request-expense-card-reimburse',
        id: 'request-expense-card-reimburse',
    };
    protected readonly reimbursementType_RadioButtons: MdsRadioButton[] = [
        this.stipendButton,
        this.reimbursementButton,
    ];

    siteService = inject(SiteStateService);
    enableRequestTypeButtons$ = this.siteService
        .getSite()
        .pipe(
            filter(Boolean),
            tap((site) => {
                this.reimbursementButton.disabled = !site.services.reimbursement;
                this.stipendButton.disabled = !site.services.stipend;
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    statusId$ = merge(this.reimbursementRequest$, this.stipendRequest$).pipe(
        filter(Boolean),
        map((request) => {
            return request.status?.statusId;
        })
    );

    showEditSaveButton$ = combineLatest({
        showEditSaveButton: toObservable(this.showEditSaveButton),
        statusId: this.statusId$,
        elevatedAccess: this.userService.permissions.request.access.elevated$,
    }).pipe(
        map(({ showEditSaveButton, statusId, elevatedAccess }) => {
            if (!statusId) return false;

            if (elevatedAccess) {
                const elevatedAccessStatuses = [
                    // admin should see Edit button in those statuses
                    PaymentRequestStatus.PendingApproval,
                    PaymentRequestStatus.PendingPSApproval,
                    PaymentRequestStatus.PaymentInProcess,
                    PaymentRequestStatus.SetUpFailed,
                    PaymentRequestStatus.PendingSetUp,
                    PaymentRequestStatus.Submitted,
                    PaymentRequestStatus.PendingSiteVerification,
                    PaymentRequestStatus.Draft,
                    PaymentRequestStatus.PayoutIssue,
                ].includes(statusId); // isPendingPSApprovalOrLower

                return showEditSaveButton && elevatedAccessStatuses;
            } else {
                const basicAccessStatuses = [
                    // CRC should see Edit button in those statuses
                    PaymentRequestStatus.Submitted,
                    PaymentRequestStatus.PendingSiteVerification,
                    PaymentRequestStatus.Draft,
                ].includes(statusId);
                return showEditSaveButton && basicAccessStatuses;
            }
        })
    );

    push(request: ReimbursementRequest | StipendRequest): ReimbursementRequest | StipendRequest {
        if (this.formGrp().controls.type.value === 'out-of-pocket') {
            return this.reimbursementRequestEditComponent().push(request as ReimbursementRequest);
        } else {
            return this.stipendRequestEditComponent().push(request as StipendRequest);
        }
    }
    saveEvent = output<Reimbursement>();
    getReimbursement() {
        if (this.formGrp().controls.type.value === 'out-of-pocket') {
            let request = this.reimbursementRequest();
            if (!request) {
                request = {
                    id: 0,
                } as ReimbursementRequest;
            }
            request = this.push(request) as ReimbursementRequest;

            const changedRequestType =
                !!this.reimbursementRequest() &&
                !!this.switchedStipendRequest() &&
                !this.switchedReimbursementRequest()?.id;

            return new Reimbursement({
                type: ReimbursementTypes.OutOfPocket,
                currency: request.totalCurrency,
                totalAmount: request.totalAmount,
                authorizedAmount: request.totalAmountAuthorized,
                totalAuthorizedAmountUsd: request.totalAmountAuthorizedUsd,
                totalAuthorizedAmountPatientsCurrency: request.totalAmountAuthorizedPatientsCurrency,
                deducted: request.deducted,
                deductedCurrency: request.deductedCurrency,
                deductedUsd: request.deductedUsd,
                expenses: request.reimbursementTransactions,
                changedRequestType,
                transactionDate: request.transactionDate,
                merchant: request.merchant,
                notes: null, // for Reimbursement request notes are in expenses
            });
        } else {
            let request = this.stipendRequest();
            if (!request) {
                request = {
                    id: 0,
                } as StipendRequest;
            }
            request = this.push(request) as StipendRequest;

            const changedRequestType =
                !!this.stipendRequest() && !!this.switchedReimbursementRequest() && !this.switchedStipendRequest()?.id;

            return new Reimbursement({
                type: ReimbursementTypes.Stipend,
                currency: request.currency,
                totalAmount: request.amount,
                authorizedAmount: request.authorizedAmount,
                authorizedAmountUsd: request.authorizedAmountUsd,
                authorizedAmountPatientsCurrency: request.authorizedAmountPatientsCurrency,
                exchangeRateDate: request.exchangeRateDate,
                deducted: request.deducted,
                deductedCurrency: request.deductedCurrency,
                deductedUsd: request.deductedUsd,
                expenses: null,
                changedRequestType,
                transactionDate: request.transactionDate,
                merchant: request.merchant,
                notes: request.notes,
            });
        }
    }
    changeModeFn = (mode: CardMode, setModeFn: (mode: CardMode) => void) => {
        if (mode === 'edit') {
            setModeFn(mode);
        } else {
            if (!this.formGrp().valid) {
                this.formGrp().markAllAsTouched();
                return;
            }

            setModeFn(mode);
        }
    };

    save() {
        this.saveEvent.emit(this.getReimbursement());
        this.switchedReimbursementRequest.set(undefined);
        this.switchedStipendRequest.set(undefined);
    }

    type$ = this.formGrp$.pipe(switchMap((formGrp) => formGrp.controls.type.valueChangesReplay));

    private readonly switchType$ = this.type$.pipe(
        filter(Boolean),
        tap((type) => {
            if (type === 'out-of-pocket') {
                this.formGrp().controls.reimbursement.enable({ emitEvent: true, force: true });
                this.formGrp().controls.stipend.disable({ emitEvent: true, persistent: true });
            } else if (type === 'stipend') {
                this.formGrp().controls.reimbursement.disable({ emitEvent: true, persistent: true });
                this.formGrp().controls.stipend.enable({ emitEvent: true, force: true });
            }
        }),
        pairwise(),
        filter(([prev, curr]) => prev !== curr),
        map(([_, type]) => type)
    );

    private readonly defaultCurrency$ = inject(PatientStateService)
        .getPatient()
        .pipe(
            take(1),
            map((patient: Patient) => patient?.preferredCurrency ?? 'USD'),
            shareReplay(1)
        );

    private readonly switchedStipendRequest: WritableSignal<StipendRequest | undefined> = signal(undefined);
    private readonly switchedReimbursementRequest: WritableSignal<ReimbursementRequest | undefined> = signal(undefined);
    private readonly handleSwitchType = this.switchType$
        .pipe(
            withLatestFrom(this.defaultCurrency$),
            tap(([type, currency]) => {
                if (type === 'out-of-pocket') {
                    if (!this.switchedStipendRequest()) {
                        this.switchedStipendRequest.set(this.stipendRequest());
                    }
                    this.reimbursementRequestService.set(
                        this.switchedReimbursementRequest() ??
                            ({
                                createdTimestamp: this.switchedStipendRequest()?.createdTimestamp,
                                submittedTimestamp: this.switchedStipendRequest()?.submittedTimestamp,
                                status: this.switchedStipendRequest()?.status,
                                totalCurrency: currency,
                            } as ReimbursementRequest)
                    );
                } else if (type === 'stipend') {
                    if (!this.switchedReimbursementRequest()) {
                        this.switchedReimbursementRequest.set(this.reimbursementRequest());
                    }

                    this.stipendRequestService.set(
                        this.switchedStipendRequest() ??
                            ({
                                createdTimestamp: this.switchedReimbursementRequest()?.createdTimestamp,
                                submittedTimestamp: this.switchedReimbursementRequest()?.submittedTimestamp,
                                status: this.switchedReimbursementRequest()?.status,
                                currency: currency,
                            } as StipendRequest)
                    );
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    onModeChange = effect(() => {
        const mode = this.linkedMode();
        if (!mode) return;

        if (mode === 'view') this.formGrp().disable({ emitEvent: true, persistent: true });
        else if (mode === 'edit') this.formGrp().enable({ emitEvent: true, force: true });
    });
    patch$ = merge(
        this.reimbursementRequest$.pipe(
            filter(Boolean),
            map(() => 'out-of-pocket' as const)
        ),
        this.stipendRequest$.pipe(
            filter(Boolean),
            map(() => 'stipend' as const)
        )
    )
        .pipe(
            tap((type) => {
                this.formGrp().patchValue(
                    {
                        type,
                    },
                    { emitEvent: true }
                );
            }),
            takeUntilDestroyed()
        )
        .subscribe();
}

export class PaymentRequestCardFormGroup extends PersistentFormGroup<{
    type: PersistentFormControl<'stipend' | 'out-of-pocket'>;
    reimbursement: ReimbursementRequestFormGroup;
    stipend: StipendRequestFormGroup;
}> {
    constructor() {
        super({
            type: new PersistentFormControl<'stipend' | 'out-of-pocket'>(null, [Validators.required]),
            reimbursement: new ReimbursementRequestFormGroup(),
            stipend: new StipendRequestFormGroup(),
        });
    }
}
